/**
 * HubEmailChannel - Email channel view with folder tree
 *
 * Shows IMAP folders in sidebar and messages in main area.
 * Supports folder management (create, rename, delete).
 */

import { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Mail,
  Inbox,
  Send,
  Archive,
  Trash2,
  AlertCircle,
  Folder,
  FolderPlus,
  ChevronRight,
  ChevronDown,
  RefreshCw,
  ArrowLeft,
  Paperclip,
  MoreVertical,
  Loader2,
  Star,
  Eye,
  EyeOff,
  FolderInput,
  Pencil,
  Trash,
  Check,
  CheckSquare,
  Square,
  MailOpen,
  ShieldAlert,
  X,
} from 'lucide-react';
import {
  getImapStatus,
  connectImap,
  sendImapConfig,
  listFolders,
  listMessages,
  getMessage,
  createFolder,
  renameFolder,
  deleteFolder,
  moveMessage,
  deleteMessage,
  markMessageRead,
  flagMessage,
  graphqlMutation,
  // Marta email categories and classification
  getEmailCategories,
  classifyEmail,
  getRecentClassifications,
  moveEmailToCategory,
  correctClassification,
  type ImapFolder,
  type ImapMessage,
  type ImapMessageFull,
  type EmailCategory,
  type EmailClassification,
  type ClassifiedEmail,
} from '@business-assistant/shared';
import { useAppShell } from '../../layouts/AppShell';
import { DetailPanel } from '../common/base';
import { useTranslation } from '../../hooks';
import { renderEntityRow } from '../common/registry/ComponentRouter';
import type { Email } from '../rows/EmailRow';

// Convert IMAP message to standardized Email format for renderEntityRow
function imapMessageToEmail(msg: ImapMessage, classification?: string, folder: string = 'INBOX'): Email {
  const fromStr = msg.from
    ? (msg.from.name ? `${msg.from.name} <${msg.from.address}>` : msg.from.address)
    : undefined;
  const toStr = msg.to.length > 0
    ? (msg.to[0].name ? `${msg.to[0].name} <${msg.to[0].address}>` : msg.to[0].address)
    : undefined;

  return {
    id: `eml-${msg.uid}`,
    from: fromStr,
    to: toStr,
    subject: msg.subject || '(No subject)',
    body: msg.snippet,
    direction: 'in' as const,
    at: msg.date,
    read: msg.flags.includes('\\Seen'),
    hasAttachments: msg.hasAttachments,
    folder: folder,
    // Pass existing classification from M.E.A. batch processing
    existingClassification: classification as Email['existingClassification'],
    existingConfidence: classification ? 0.85 : undefined,
  };
}

// =============================================================================
// TYPES
// =============================================================================

interface FolderNode extends ImapFolder {
  children: FolderNode[];
  level: number;
  isExpanded?: boolean;
}

/** Email category slugs for URL routing */
type EmailCategorySlug = 'important' | 'routine' | 'ads' | 'spam' | 'classifying';

interface HubEmailChannelProps {
  onBack: () => void;
  initialFolder?: string;
  /** Initial category from URL (important, routine, ads, spam, classifying) */
  initialCategory?: EmailCategorySlug;
  /** Currently selected email ID from URL */
  selectedEmailId?: string;
  /** Called when user selects an email (updates URL) */
  onSelectEmail?: (emailId: string) => void;
  /** Called when user closes email widget (updates URL) */
  onCloseEmail?: () => void;
  /** Called when folder changes (updates URL) */
  onFolderChange?: (folder: string) => void;
  /** Called when category changes (updates URL) */
  onCategoryChange?: (category: EmailCategorySlug | null) => void;
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Convert technical folder names to user-friendly English names
 */
function getFriendlyFolderName(folder: ImapFolder): string {
  const path = folder.path.toLowerCase();
  const specialUse = folder.specialUse?.toLowerCase() || '';

  // Main folders - use English names (default)
  if (specialUse.includes('inbox') || path === 'inbox') return 'Inbox';
  if (specialUse.includes('sent') || path.includes('sent') || path.includes('inviata')) return 'Sent';
  if (specialUse.includes('drafts') || path.includes('draft') || path.includes('bozze')) return 'Drafts';
  if (specialUse.includes('trash') || path.includes('trash') || path.includes('cestino')) return 'Trash';
  if (specialUse.includes('archive') || path.includes('archive') || path.includes('archivio')) return 'Archive';
  // Spam folder (check BEFORE junk - INBOX.Spam should show as "Spam")
  if (path.includes('spam') || path.endsWith('.spam') || path === 'spam' || path === 'inbox.spam') return 'Spam';
  // Junk folder (separate from spam)
  if (specialUse.includes('junk') || path.endsWith('.junk') || path === 'junk') return 'Junk';

  // For subfolders like INBOX.notes, extract just the subfolder name
  if (path.includes('.')) {
    const parts = folder.path.split('.');
    const lastPart = parts[parts.length - 1];
    // Capitalize first letter
    return lastPart.charAt(0).toUpperCase() + lastPart.slice(1);
  }

  // Use label if available, otherwise folder name
  return folder.label || folder.name;
}

/**
 * Check if a folder should be hidden from the main list
 */
function shouldHideFolder(folder: ImapFolder): boolean {
  const path = folder.path.toLowerCase();

  // Hide internal/system folders
  if (path.includes('[gmail]') || path.includes('[google mail]')) return true;
  if (path === 'notes' || path.endsWith('.notes')) return true; // Hide notes folders

  // Show both Junk and Spam folders - they may contain different emails
  // (Previously we hid Spam to avoid duplicates, but user needs both)

  return false;
}

/**
 * Check if folder is a "main" folder (not a subfolder)
 */
function isMainFolder(folder: ImapFolder): boolean {
  const path = folder.path.toLowerCase();
  const specialUse = folder.specialUse?.toLowerCase() || '';

  // These are always main folders
  if (specialUse.includes('inbox') || path === 'inbox') return true;
  if (specialUse.includes('sent')) return true;
  if (specialUse.includes('drafts')) return true;
  if (specialUse.includes('trash')) return true;
  if (specialUse.includes('archive')) return true;
  if (specialUse.includes('junk')) return true;

  // Subfolders (contain . or /) are not main
  if (path.includes('.') || path.includes('/')) return false;

  return true;
}

function buildFolderTree(folders: ImapFolder[]): FolderNode[] {
  // Sort folders by path to ensure parent comes before child
  const sorted = [...folders].sort((a, b) => a.path.localeCompare(b.path));
  const tree: FolderNode[] = [];
  const nodeMap = new Map<string, FolderNode>();

  for (const folder of sorted) {
    const node: FolderNode = {
      ...folder,
      children: [],
      level: 0,
      isExpanded: folder.specialUse === '\\Inbox' || folder.path === 'INBOX',
    };

    // Find parent path
    const pathParts = folder.path.split('/');
    if (pathParts.length > 1) {
      const parentPath = pathParts.slice(0, -1).join('/');
      const parent = nodeMap.get(parentPath);
      if (parent) {
        node.level = parent.level + 1;
        parent.children.push(node);
      } else {
        tree.push(node);
      }
    } else {
      tree.push(node);
    }

    nodeMap.set(folder.path, node);
  }

  return tree;
}

function getFolderIcon(folder: FolderNode): React.ReactNode {
  const specialUse = folder.specialUse?.toLowerCase();
  const path = folder.path.toLowerCase();

  if (specialUse?.includes('inbox') || path === 'inbox') return <Inbox size={16} />;
  if (specialUse?.includes('sent') || path.includes('sent')) return <Send size={16} />;
  if (specialUse?.includes('archive') || path.includes('archive')) return <Archive size={16} />;
  if (specialUse?.includes('trash') || path.includes('trash')) return <Trash2 size={16} />;
  // Both Junk and Spam use warning icon but are distinct folders
  if (specialUse?.includes('junk') || path.endsWith('.junk') || path === 'junk') return <ShieldAlert size={16} />;
  if (path.endsWith('.spam') || path === 'spam') return <AlertCircle size={16} />;

  return <Folder size={16} />;
}

function formatEmailDate(isoDate: string, language: string, t: (key: string, params?: Record<string, unknown>) => string): string {
  const date = new Date(isoDate);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / 86400000);
  const locale = language === 'it' ? 'it-IT' : 'en-US';

  if (diffDays === 0) {
    return date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  }
  if (diffDays === 1) return t('common.yesterday');
  if (diffDays < 7) return t('common.daysAgo', { count: diffDays });
  return date.toLocaleDateString(locale, { day: '2-digit', month: 'short' });
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

// Map category slug to internal category ID
const categorySlugToId: Record<EmailCategorySlug, string> = {
  important: 'cat-important',
  routine: 'cat-routine',
  ads: 'cat-ads',
  spam: 'cat-spam',
  classifying: 'cat-classifying',
};

// Map internal category ID to slug
const categoryIdToSlug: Record<string, EmailCategorySlug> = {
  'cat-important': 'important',
  'cat-routine': 'routine',
  'cat-ads': 'ads',
  'cat-spam': 'spam',
  'cat-classifying': 'classifying',
};

export function HubEmailChannel({
  onBack,
  initialFolder = 'INBOX',
  initialCategory,
  selectedEmailId,
  onSelectEmail,
  onCloseEmail,
  onFolderChange,
  onCategoryChange,
}: HubEmailChannelProps) {
  const { t, language } = useTranslation();
  const { showToast, setDrawerSectionsOverride } = useAppShell();

  // Connection state
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  // Folder state - controlled by URL when onFolderChange is provided
  const [folders, setFolders] = useState<ImapFolder[]>([]);
  const [selectedFolder, setSelectedFolderLocal] = useState(initialFolder);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set(['INBOX']));
  const [isLoadingFolders, setIsLoadingFolders] = useState(false);

  // Marta categories state - controlled by URL when onCategoryChange is provided
  const [categories, setCategories] = useState<EmailCategory[]>([]);
  const [selectedCategory, setSelectedCategoryLocal] = useState<string | null>(
    initialCategory ? categorySlugToId[initialCategory] : null
  );

  // Email classifications cache (uid -> classification)
  const [emailClassifications, setEmailClassifications] = useState<Map<number, string>>(new Map());

  // Flag to track if classifications have been loaded (for deep link support)
  const [classificationsReady, setClassificationsReady] = useState(false);

  // Wrapper to handle category changes - updates URL if callback provided
  const setSelectedCategory = useCallback((categoryId: string | null) => {
    setSelectedCategoryLocal(categoryId);
    if (onCategoryChange) {
      const slug = categoryId ? categoryIdToSlug[categoryId] : null;
      onCategoryChange(slug || null);
    }
  }, [onCategoryChange]);

  // Sync category with URL (initialCategory from parent)
  // Also handles clearing category when URL changes from /hub/email/INBOX/important to /hub/email/INBOX
  useEffect(() => {
    if (initialCategory) {
      const categoryId = categorySlugToId[initialCategory];
      if (categoryId && categoryId !== selectedCategory) {
        setSelectedCategoryLocal(categoryId);
      }
    } else if (selectedCategory && !initialCategory) {
      // URL no longer has category, clear local state
      setSelectedCategoryLocal(null);
    }
  }, [initialCategory, selectedCategory]);

  // Sync folder with URL (initialFolder from parent)
  useEffect(() => {
    if (initialFolder && initialFolder !== selectedFolder) {
      setSelectedFolderLocal(initialFolder);
    }
  }, [initialFolder]);

  // Helper to set folder - updates URL if available
  const setSelectedFolder = (folder: string) => {
    setSelectedFolderLocal(folder);
    onFolderChange?.(folder);
  };

  // Message state
  const [messages, setMessages] = useState<ImapMessage[]>([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<ImapMessage | null>(null);

  // UI state
  const [showNewFolderDialog, setShowNewFolderDialog] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderParent, setNewFolderParent] = useState<string | null>(null);
  const [folderMenuOpen, setFolderMenuOpen] = useState<string | null>(null);

  // Email selection state
  const [selectionMode, setSelectionMode] = useState(false); // Bulk selection mode
  const [selectedEmails, setSelectedEmails] = useState<Set<number>>(new Set());
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [draggingEmailUid, setDraggingEmailUid] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false); // Track if actual drag happened

  // Build folder tree
  const folderTree = useMemo(() => buildFolderTree(folders), [folders]);

  // ==========================================================================
  // EFFECTS
  // ==========================================================================

  // Check connection on mount
  useEffect(() => {
    checkConnection();
  }, []);

  // Load classifications and categories FIRST when connected (before messages)
  useEffect(() => {
    if (isConnected) {
      // Load classifications and categories in parallel, before loading messages
      Promise.all([
        loadClassifications(),
        loadCategories(),
      ]).then(() => {
        console.log('[M.E.A.] Classifications and categories loaded');
        setClassificationsReady(true);
      });
    }
  }, [isConnected]);

  // Load messages when folder changes AND classifications are ready
  // This ensures deep links like /hub/email/INBOX/important work correctly
  useEffect(() => {
    if (isConnected && selectedFolder && classificationsReady) {
      loadMessages();
    }
  }, [selectedFolder, isConnected, classificationsReady]);

  const loadClassifications = async () => {
    try {
      // Get recent classifications from backend
      const classifications = await getRecentClassifications(500);
      const classMap = new Map<number, string>();
      for (const c of classifications) {
        classMap.set(c.uid, c.classification);
      }
      setEmailClassifications(classMap);
    } catch (err) {
      console.error('[M.E.A.] Failed to load classifications:', err);
    }
  };

  // Handle classification change from dropdown or drag-drop
  const handleClassificationChange = (uid: number, newClassification: string) => {
    setEmailClassifications(prev => {
      const next = new Map(prev);
      next.set(uid, newClassification);
      return next;
    });
    // Refresh categories to update counts
    loadCategories();
  };

  // Handle dropping email on a category (change classification via drag-drop)
  const handleDropOnCategory = async (e: React.DragEvent, category: EmailCategory) => {
    e.preventDefault();
    const emailUidStr = e.dataTransfer.getData('text/plain');
    const emailUid = parseInt(emailUidStr, 10);

    if (isNaN(emailUid)) {
      return;
    }

    const currentClassification = emailClassifications.get(emailUid);
    if (currentClassification === category.classification) {
      // Already in this category
      return;
    }


    // Find the email subject for the correction
    const msg = messages.find(m => m.uid === emailUid);
    const emailSubject = msg?.subject;
    const emailFrom = msg?.from?.address;

    try {
      // Save the classification change via correctClassification API
      const result = await correctClassification({
        emailId: `INBOX:${emailUid}`,
        originalClassification: currentClassification || 'unknown',
        correctedClassification: category.classification,
        emailSubject,
        emailFrom,
        correctionNote: `Trascinato nella categoria "${category.name}"`,
      });

      if (result.success) {
        // Update local classification
        handleClassificationChange(emailUid, category.classification);
        showToast?.(`✅ Email spostata in ${category.name}`);
      } else {
        console.error('[M.E.A.] Correction failed:', result.error);
        showToast?.('❌ Errore durante lo spostamento');
      }
    } catch (error) {
      console.error('[M.E.A.] Drop error:', error);
      showToast?.('❌ Errore durante lo spostamento');
    }
  };

  // Backend cronjob handles classification automatically (every 30 seconds)
  // Frontend just loads existing classifications from DB

  // Filter messages by selected category based on classification
  const filteredMessages = useMemo(() => {
    // If no category selected or not in INBOX, show all messages
    if (!selectedCategory || selectedFolder !== 'INBOX') {
      return messages;
    }

    // Special category: show emails being classified (not yet processed)
    if (selectedCategory === 'cat-classifying') {
      return messages.filter(msg => !emailClassifications.has(msg.uid));
    }

    // If classifications haven't loaded yet, show all messages
    if (emailClassifications.size === 0) {
      return messages;
    }

    // Find the selected category to get its classification value
    const category = categories.find(c => c.id === selectedCategory);

    if (!category) {
      return messages;
    }

    // Filter messages by classification
    const classificationFilter = category.classification;
    return messages.filter(msg => {
      const msgClassification = emailClassifications.get(msg.uid);
      return msgClassification === classificationFilter;
    });
  }, [messages, selectedCategory, selectedFolder, categories, emailClassifications]);

  // Calculate unclassified count
  const unclassifiedCount = useMemo(() => {
    return messages.filter(msg => !emailClassifications.has(msg.uid)).length;
  }, [messages, emailClassifications]);

  const loadCategories = async () => {
    try {
      const cats = await getEmailCategories();
      setCategories(cats);
    } catch (err) {
      console.error('[M.E.A.] Failed to load categories:', err);
    }
  };


  // Update drawer with folder tree + Marta categories as children of Inbox
  useEffect(() => {
    if (folders.length === 0) return;

    // Helper to get icon for folder
    const getFolderIconName = (folder: typeof folderTree[0]): string => {
      const path = folder.path.toLowerCase();
      const specialUse = folder.specialUse?.toLowerCase() || '';
      if (specialUse.includes('inbox') || path === 'inbox') return 'inbox';
      if (specialUse.includes('sent') || path.includes('sent')) return 'send';
      if (specialUse.includes('drafts') || path.includes('draft')) return 'file-text';
      if (specialUse.includes('trash') || path.includes('trash')) return 'trash-2';
      if (specialUse.includes('archive') || path.includes('archive')) return 'archive';
      // Junk and Spam are distinct folders
      if (specialUse.includes('junk') || path.endsWith('.junk') || path === 'junk') return 'shield-alert';
      if (path.endsWith('.spam') || path === 'spam') return 'alert-circle';
      return 'folder';
    };

    // Check if folder is Inbox
    const isInbox = (folder: typeof folderTree[0]): boolean => {
      const path = folder.path.toLowerCase();
      const specialUse = folder.specialUse?.toLowerCase() || '';
      return specialUse.includes('inbox') || path === 'inbox';
    };

    // Filter folders - hide technical ones
    const visibleFolders = folderTree.filter(folder => !shouldHideFolder(folder));

    // Build Marta category children for Inbox (all categories including spam)
    // The spam category here is M.E.A.'s classification, different from IMAP Spam folder
    const categoryChildren: Array<{
      id: string;
      label: string;
      hint: string;
      icon: string;
      badge?: number;
      active: boolean;
      isDropTarget?: boolean;
      onClick: () => void;
      onDrop?: (e: React.DragEvent) => void;
      onDragOver?: (e: React.DragEvent) => void;
    }> = [];

    // Add M.E.A. categories (no "All" - clicking INBOX shows all emails)
    categories.forEach(cat => {
      // Show count in format: "unread/total" or just total if no unread
      let countText = '';
      if (cat.totalCount > 0) {
        countText = cat.unreadCount > 0
          ? ` (${cat.unreadCount}/${cat.totalCount})`
          : ` (${cat.totalCount})`;
      }
      const labelWithCount = `${cat.icon} ${cat.name}${countText}`;

      categoryChildren.push({
        id: cat.id,
        label: labelWithCount,
        hint: '',
        icon: 'tag',
        badge: undefined,
        active: selectedCategory === cat.id,
        isDropTarget: true,
        onClick: () => {
          setSelectedCategory(cat.id);
          setSelectedFolder('INBOX');
        },
        onDrop: (e: React.DragEvent) => handleDropOnCategory(e, cat),
        onDragOver: (e: React.DragEvent) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
        },
      });
    });

    // Show "Classifying..." for emails not yet processed by backend cronjob
    // This category disappears when all emails are classified
    if (unclassifiedCount > 0) {
      categoryChildren.push({
        id: 'cat-classifying',
        label: `⏳ Classifying... (${unclassifiedCount})`,
        hint: 'M.E.A.',
        icon: 'loader',
        badge: undefined,
        active: selectedCategory === 'cat-classifying',
        onClick: () => {
          // Show unclassified emails while they're being processed
          setSelectedCategory('cat-classifying');
          setSelectedFolder('INBOX');
        },
      });
    }

    // Build folder items - Inbox gets categories as children
    const folderItems = visibleFolders.map(folder => {
      const friendlyName = getFriendlyFolderName(folder);
      // Show unread count in parentheses next to label (not in hint)
      const labelWithCount = folder.messagesUnread > 0
        ? `${friendlyName} (${folder.messagesUnread})`
        : friendlyName;

      // For Inbox: stay active when a category is selected (so children remain visible)
      const isInboxWithCategorySelected = isInbox(folder) && selectedCategory !== null;
      const itemIsActive = folder.path === selectedFolder && !selectedCategory;

      const item: {
        id: string;
        label: string;
        hint: string;
        icon: string;
        badge?: number;
        active: boolean;
        isDropTarget: boolean;
        onClick: () => void;
        children?: typeof categoryChildren;
      } = {
        id: folder.path,
        label: labelWithCount,
        hint: '', // No description in hint
        icon: getFolderIconName(folder),
        badge: undefined, // Don't use badge, count is in label
        active: itemIsActive || isInboxWithCategorySelected,  // Keep INBOX "active" to show children
        isDropTarget: true, // All folders can receive drops
        onClick: () => {
          // For INBOX: default to "Importante" category, for other folders: no category filter
          setSelectedCategory(null); // Clear category, INBOX shows all emails
          setSelectedFolder(folder.path);
        },
      };

      // Add Marta categories as children of Inbox
      if (isInbox(folder) && categoryChildren.length > 0) {
        item.children = categoryChildren;
      }

      return item;
    });

    const sections = [
      {
        title: 'Folders',
        items: folderItems,
      },
      {
        title: 'Actions',
        items: [
          {
            id: 'new-folder',
            label: 'New folder',
            hint: 'Create a folder',
          icon: 'folder-plus',
          onClick: () => {
            setNewFolderParent(null);
            setShowNewFolderDialog(true);
          },
        },
          {
            id: 'back',
            label: 'Back to channels',
            hint: 'Return to channel list',
            icon: 'arrow-left',
            onClick: onBack,
          },
        ],
      },
    ];

    setDrawerSectionsOverride?.(sections);

    return () => setDrawerSectionsOverride?.(null);
  }, [folders, folderTree, selectedFolder, selectedCategory, categories, setDrawerSectionsOverride, onBack, messages.length, unclassifiedCount]);

  // ==========================================================================
  // API CALLS
  // ==========================================================================

  // Load email integration config from database using the credentials mutation
  const loadEmailConfig = async () => {
    try {
      const query = `
        mutation GetEmailCredentials {
          getEmailCredentials {
            id
            email
            imapHost
            imapPort
            imapTls
            smtpHost
            smtpPort
            smtpSecure
            password
          }
        }
      `;
      const result = await graphqlMutation<{ getEmailCredentials: {
        id: string;
        email: string;
        imapHost: string;
        imapPort: number;
        imapTls: boolean;
        smtpHost: string;
        smtpPort: number;
        smtpSecure: boolean;
        password: string;
      } | null }>(query);

      const creds = result.getEmailCredentials;
      if (creds && creds.imapHost && creds.password) {
        return {
          imap: {
            host: creds.imapHost,
            port: creds.imapPort || 993,
            user: creds.email,
            password: creds.password,
            tls: creds.imapTls !== false,
          },
          smtp: {
            host: creds.smtpHost || creds.imapHost,
            port: creds.smtpPort || 587,
            user: creds.email,
            password: creds.password,
            secure: creds.smtpSecure === true,
          },
        };
      }
      return null;
    } catch (err) {
      console.error('Error loading email config:', err);
      return null;
    }
  };

  const checkConnection = async () => {
    try {
      setIsConnecting(true);
      setConnectionError(null);
      const status = await getImapStatus();
      setIsConnected(status.imap.connected);

      if (status.imap.connected) {
        await loadFolders();
      } else {
        await handleConnect();
      }
    } catch (err) {
      setConnectionError(err instanceof Error ? err.message : t('email.connectionFailed'));
    } finally {
      setIsConnecting(false);
    }
  };

  const handleConnect = async () => {
    try {
      setIsConnecting(true);
      setConnectionError(null);

      // Load config from database and send to IMAP server
      const config = await loadEmailConfig();
      if (!config) {
        setConnectionError(t('email.noConfigured'));
        return;
      }

      // Send config and connect
      await connectImap(config);
      setIsConnected(true);
      await loadFolders();
    } catch (err) {
      setConnectionError(err instanceof Error ? err.message : t('email.connectionFailed'));
    } finally {
      setIsConnecting(false);
    }
  };

  const loadFolders = async () => {
    try {
      setIsLoadingFolders(true);
      const result = await listFolders();
      setFolders(result);
    } catch (err) {
      console.error('Error loading folders:', err);
    } finally {
      setIsLoadingFolders(false);
    }
  };

  const loadMessages = async () => {
    try {
      setIsLoadingMessages(true);
      // Load messages - temporarily limited to 20 for testing
      const limit = selectedFolder === 'INBOX' ? 20 : 20;
      const result = await listMessages(selectedFolder, limit);
      setMessages(result);
    } catch (err) {
      console.error('Error loading messages:', err);
      showToast(t('email.loadMessagesError'));
    } finally {
      setIsLoadingMessages(false);
    }
  };

  const handleSelectMessage = (uid: number) => {
    // Update URL - GlobalEntityModal will handle rendering EmailWidget
    const emailId = `eml-${uid}`;
    onSelectEmail?.(emailId);

    // Mark as read in local list state (optimistic update)
    setMessages(prev => prev.map(m =>
      m.uid === uid ? { ...m, flags: [...m.flags, '\\Seen'] } : m
    ));
  };

  // ==========================================================================
  // FOLDER ACTIONS
  // ==========================================================================

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;

    const path = newFolderParent
      ? `${newFolderParent}/${newFolderName.trim()}`
      : newFolderName.trim();

    try {
      await createFolder(path);
      showToast(t('email.folderCreated', { name: newFolderName }));
      setShowNewFolderDialog(false);
      setNewFolderName('');
      setNewFolderParent(null);
      await loadFolders();
    } catch (err) {
      showToast(t('email.folderCreateError'));
    }
  };

  const handleDeleteFolder = async (path: string) => {
    if (!confirm(t('email.confirmDeleteFolder', { name: path }))) return;

    try {
      await deleteFolder(path);
      showToast(t('email.folderDeleted'));
      await loadFolders();
      if (selectedFolder === path) {
        setSelectedFolder('INBOX');
      }
    } catch (err) {
      showToast(t('email.folderDeleteError'));
    }
  };

  const handleRenameFolder = async (oldPath: string) => {
    const newName = prompt(t('email.newFolderName'), oldPath.split('/').pop());
    if (!newName) return;

    const parentPath = oldPath.includes('/') ? oldPath.split('/').slice(0, -1).join('/') : '';
    const newPath = parentPath ? `${parentPath}/${newName}` : newName;

    try {
      await renameFolder(oldPath, newPath);
      showToast(t('email.folderRenamed'));
      await loadFolders();
      if (selectedFolder === oldPath) {
        setSelectedFolder(newPath);
      }
    } catch (err) {
      showToast(t('email.folderRenameError'));
    }
  };

  // ==========================================================================
  // MESSAGE ACTIONS
  // ==========================================================================

  const handleMoveMessage = async (uid: number, toFolder: string) => {
    try {
      await moveMessage(uid, selectedFolder, toFolder);
      showToast(t('email.messageMoved'));
      setMessages(prev => prev.filter(m => m.uid !== uid));
      if (selectedMessage?.uid === uid) {
        setSelectedMessage(null);
      }
    } catch (err) {
      showToast(t('email.moveError'));
    }
  };

  const handleDeleteMessage = async (uid: number) => {
    try {
      await deleteMessage(uid, selectedFolder);
      showToast(t('email.messageDeleted'));
      setMessages(prev => prev.filter(m => m.uid !== uid));
      if (selectedMessage?.uid === uid) {
        setSelectedMessage(null);
      }
    } catch (err) {
      showToast(t('email.deleteError'));
    }
  };

  const handleToggleRead = async (uid: number, currentlyRead: boolean) => {
    try {
      await markMessageRead(uid, selectedFolder, !currentlyRead);
      setMessages(prev => prev.map(m =>
        m.uid === uid
          ? { ...m, flags: currentlyRead ? m.flags.filter(f => f !== '\\Seen') : [...m.flags, '\\Seen'] }
          : m
      ));
    } catch (err) {
      showToast(t('email.error'));
    }
  };

  const handleToggleFlag = async (uid: number, currentlyFlagged: boolean) => {
    try {
      await flagMessage(uid, selectedFolder, !currentlyFlagged);
      setMessages(prev => prev.map(m =>
        m.uid === uid
          ? { ...m, flags: currentlyFlagged ? m.flags.filter(f => f !== '\\Flagged') : [...m.flags, '\\Flagged'] }
          : m
      ));
    } catch (err) {
      showToast(t('email.error'));
    }
  };

  // ==========================================================================
  // EMAIL SELECTION & MOVE
  // ==========================================================================

  const handleToggleEmailSelection = (uid: number) => {
    setSelectedEmails(prev => {
      const next = new Set(prev);
      if (next.has(uid)) {
        next.delete(uid);
      } else {
        next.add(uid);
      }
      return next;
    });
  };

  const handleSelectAllEmails = () => {
    if (selectedEmails.size === filteredMessages.length) {
      setSelectedEmails(new Set());
    } else {
      setSelectedEmails(new Set(filteredMessages.map(m => m.uid)));
    }
  };

  const handleMoveEmails = async (targetFolder: string) => {
    const emailsToMove = Array.from(selectedEmails);
    if (emailsToMove.length === 0) return;

    try {
      for (const uid of emailsToMove) {
        await moveMessage(uid, selectedFolder, targetFolder);
      }
      showToast?.(`✅ ${emailsToMove.length} email spostate in ${getFriendlyFolderName({ path: targetFolder, name: targetFolder } as ImapFolder)}`);
      setSelectedEmails(new Set());
      setShowMoveDialog(false);
      loadMessages();
    } catch (err) {
      showToast?.('❌ Errore durante lo spostamento');
    }
  };

  const handleDragStart = (e: React.DragEvent, uid: number) => {
    setDraggingEmailUid(uid);
    setIsDragging(true);
    e.dataTransfer.setData('text/plain', uid.toString());
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDraggingEmailUid(null);
    // Reset isDragging after a small delay to allow click to check it
    setTimeout(() => setIsDragging(false), 100);
  };

  // Handle email click - only if not dragging
  const handleEmailClick = (uid: number) => {
    if (!isDragging) {
      handleSelectMessage(uid);
    }
  };

  const handleDropOnFolder = async (targetFolder: string) => {
    if (draggingEmailUid === null) return;
    if (targetFolder === selectedFolder) return;

    try {
      await moveMessage(draggingEmailUid, selectedFolder, targetFolder);
      showToast?.(`✅ Email spostata in ${getFriendlyFolderName({ path: targetFolder, name: targetFolder } as ImapFolder)}`);
      setMessages(prev => prev.filter(m => m.uid !== draggingEmailUid));
    } catch (err) {
      showToast?.('❌ Errore durante lo spostamento');
    }
    setDraggingEmailUid(null);
  };

  // Bulk actions
  const handleBulkMarkRead = async (markAsRead: boolean) => {
    const emailsToMark = Array.from(selectedEmails);
    if (emailsToMark.length === 0) return;

    try {
      for (const uid of emailsToMark) {
        await markMessageRead(uid, selectedFolder, markAsRead);
      }
      setMessages(prev => prev.map(m =>
        selectedEmails.has(m.uid)
          ? { ...m, flags: markAsRead ? [...m.flags.filter(f => f !== '\\Seen'), '\\Seen'] : m.flags.filter(f => f !== '\\Seen') }
          : m
      ));
      showToast?.(`✅ ${emailsToMark.length} email segnate come ${markAsRead ? 'lette' : 'non lette'}`);
      setSelectedEmails(new Set());
    } catch (err) {
      showToast?.('❌ Errore');
    }
  };

  const handleBulkDelete = async () => {
    const emailsToDelete = Array.from(selectedEmails);
    if (emailsToDelete.length === 0) return;

    try {
      for (const uid of emailsToDelete) {
        await deleteMessage(uid, selectedFolder);
      }
      showToast?.(`✅ ${emailsToDelete.length} email eliminate`);
      setSelectedEmails(new Set());
      loadMessages();
    } catch (err) {
      showToast?.('❌ Errore durante l\'eliminazione');
    }
  };

  const handleBulkArchive = async () => {
    // Find archive folder
    const archiveFolder = folders.find(f =>
      f.specialUse?.includes('Archive') || f.path.toLowerCase().includes('archive')
    );
    if (archiveFolder) {
      await handleMoveEmails(archiveFolder.path);
    } else {
      showToast?.('❌ Cartella Archivio non trovata');
    }
  };

  const handleBulkSpam = async () => {
    // Find spam folder
    const spamFolder = folders.find(f =>
      f.specialUse?.includes('Junk') || f.path.toLowerCase().includes('spam') || f.path.toLowerCase().includes('junk')
    );
    if (spamFolder) {
      await handleMoveEmails(spamFolder.path);
    } else {
      showToast?.('❌ Cartella Spam non trovata');
    }
  };

  // ==========================================================================
  // RENDER
  // ==========================================================================

  // Connection error screen
  if (connectionError && !isConnected) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <AlertCircle size={48} style={{ color: '#ef4444', marginBottom: '1rem' }} />
        <h3>{t('email.connectionError')}</h3>
        <p style={{ color: 'var(--text-muted)' }}>{connectionError}</p>
        <button
          onClick={handleConnect}
          disabled={isConnecting}
          style={{
            marginTop: '1rem',
            padding: '0.5rem 1rem',
            background: 'var(--brand)',
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            cursor: 'pointer',
          }}
        >
          {isConnecting ? t('email.connecting') : t('email.retry')}
        </button>
      </div>
    );
  }

  // Loading screen
  if (isConnecting) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} />
        <p style={{ marginTop: '1rem', color: 'var(--text-muted)' }}>{t('email.connectingToServer')}</p>
      </div>
    );
  }

  const hasSelection = selectedEmails.size > 0;

  // Exit selection mode when all emails are deselected
  const exitSelectionMode = () => {
    setSelectionMode(false);
    setSelectedEmails(new Set());
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Email Toolbar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.5rem 1rem',
          borderBottom: '1px solid var(--line)',
          background: selectionMode ? '#eff6ff' : 'var(--bg)',
          transition: 'background 0.15s',
        }}
      >
        {/* Show different toolbar based on selection mode */}
        {!selectionMode ? (
          <>
            {/* Normal mode - Show Modifica button */}
            <button
              onClick={() => setSelectionMode(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.375rem',
                padding: '0.375rem 0.75rem',
                background: 'white',
                border: '1px solid var(--line)',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.8125rem',
                color: 'var(--text)',
                fontWeight: 500,
              }}
            >
              <CheckSquare size={14} />
              Modifica
            </button>

            {/* Message count */}
            <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
              {filteredMessages.length} email
              {selectedCategory && filteredMessages.length !== messages.length && (
                <span style={{ opacity: 0.6 }}> / {messages.length} totali</span>
              )}
            </span>

            {/* Refresh button */}
            <button
              onClick={loadMessages}
              disabled={isLoadingMessages}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
                padding: '0.375rem 0.625rem',
                background: 'transparent',
                border: '1px solid var(--line)',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                fontSize: '0.75rem',
                marginLeft: 'auto',
              }}
            >
              <RefreshCw size={14} style={{ animation: isLoadingMessages ? 'spin 1s linear infinite' : 'none' }} />
              Aggiorna
            </button>
          </>
        ) : (
          <>
            {/* Selection mode - Show select all checkbox and bulk actions */}
            <button
              onClick={handleSelectAllEmails}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '28px',
                height: '28px',
                background: 'transparent',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                color: hasSelection ? '#2563eb' : 'var(--text-muted)',
              }}
              title={selectedEmails.size === filteredMessages.length ? 'Deseleziona tutti' : 'Seleziona tutti'}
            >
              {selectedEmails.size === filteredMessages.length && filteredMessages.length > 0 ? (
                <CheckSquare size={18} />
              ) : selectedEmails.size > 0 ? (
                <div style={{ position: 'relative' }}>
                  <Square size={18} />
                  <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 8, height: 8, background: '#2563eb', borderRadius: 2 }} />
                </div>
              ) : (
                <Square size={18} />
              )}
            </button>

            {/* Selection count */}
            <span style={{ fontSize: '0.8125rem', color: hasSelection ? '#2563eb' : 'var(--text-muted)', fontWeight: hasSelection ? 500 : 400 }}>
              {hasSelection
                ? `${selectedEmails.size} selezionat${selectedEmails.size === 1 ? 'a' : 'e'}`
                : 'Seleziona email'}
            </span>

            {/* Bulk actions - shown when emails are selected */}
            {hasSelection && (
          <>
            <div style={{ width: '1px', height: '20px', background: '#cbd5e1', margin: '0 0.25rem' }} />

            {/* Mark as read/unread */}
            <button
              onClick={() => handleBulkMarkRead(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
                padding: '0.375rem 0.5rem',
                background: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.75rem',
                color: '#475569',
              }}
              title="Segna come letto"
            >
              <MailOpen size={14} />
              <span className="hidden-mobile">Letto</span>
            </button>

            <button
              onClick={() => handleBulkMarkRead(false)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
                padding: '0.375rem 0.5rem',
                background: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.75rem',
                color: '#475569',
              }}
              title="Segna come non letto"
            >
              <Mail size={14} />
              <span className="hidden-mobile">Non letto</span>
            </button>

            {/* Archive */}
            <button
              onClick={handleBulkArchive}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
                padding: '0.375rem 0.5rem',
                background: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.75rem',
                color: '#475569',
              }}
              title="Archivia"
            >
              <Archive size={14} />
              <span className="hidden-mobile">Archivia</span>
            </button>

            {/* Spam */}
            <button
              onClick={handleBulkSpam}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
                padding: '0.375rem 0.5rem',
                background: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.75rem',
                color: '#475569',
              }}
              title="Segnala come spam"
            >
              <ShieldAlert size={14} />
              <span className="hidden-mobile">Spam</span>
            </button>

            {/* Move to folder */}
            <button
              onClick={() => setShowMoveDialog(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
                padding: '0.375rem 0.5rem',
                background: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.75rem',
                color: '#475569',
              }}
              title="Sposta in cartella"
            >
              <FolderInput size={14} />
              <span className="hidden-mobile">Sposta</span>
            </button>

            {/* Delete */}
            <button
              onClick={handleBulkDelete}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
                padding: '0.375rem 0.5rem',
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.75rem',
                color: '#dc2626',
              }}
              title="Elimina"
            >
              <Trash2 size={14} />
              <span className="hidden-mobile">Elimina</span>
            </button>

            {/* Clear selection */}
            <button
              onClick={() => setSelectedEmails(new Set())}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '28px',
                height: '28px',
                background: 'transparent',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                color: '#64748b',
                marginLeft: 'auto',
              }}
              title="Annulla selezione"
            >
              <X size={16} />
            </button>
          </>
            )}

            {/* Exit selection mode button */}
            <button
              onClick={exitSelectionMode}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
                padding: '0.375rem 0.625rem',
                background: 'transparent',
                border: '1px solid var(--line)',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                fontSize: '0.75rem',
                marginLeft: hasSelection ? '0' : 'auto',
              }}
            >
              <X size={14} />
              Esci
            </button>
          </>
        )}
      </div>

        {/* Message list */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          {isLoadingMessages ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
              <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
              <p style={{ marginTop: '0.5rem' }}>{t('common.loading')}</p>
            </div>
          ) : filteredMessages.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
              <Mail size={32} style={{ opacity: 0.5, marginBottom: '0.5rem' }} />
              <p>
                {selectedCategory
                  ? 'No emails in this category'
                  : t('email.noMessages')}
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {filteredMessages.map(msg => {
                const isSelected = selectedEmails.has(msg.uid);
                const isBeingDragged = draggingEmailUid === msg.uid;

                // In selection mode: show checkbox + row
                // Normal mode: use standard renderEntityRow with classification badge built-in
                const msgClassification = emailClassifications.get(msg.uid);
                if (selectionMode) {
                  return (
                    <div
                      key={msg.uid}
                      draggable
                      onDragStart={(e) => handleDragStart(e, msg.uid)}
                      onDragEnd={handleDragEnd}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        background: isSelected ? '#eff6ff' : isBeingDragged ? '#f1f5f9' : 'transparent',
                        opacity: isBeingDragged ? 0.5 : 1,
                        transition: 'background 0.1s',
                        paddingLeft: '0.5rem',
                      }}
                    >
                      {/* Checkbox for selection */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleEmailSelection(msg.uid);
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '24px',
                          height: '24px',
                          background: isSelected ? '#2563eb' : 'transparent',
                          border: isSelected ? 'none' : '2px solid #d1d5db',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          color: 'white',
                          flexShrink: 0,
                        }}
                      >
                        {isSelected && <Check size={14} />}
                      </button>

                      {/* Standard email row with classification badge */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {renderEntityRow(imapMessageToEmail(msg, msgClassification, selectedFolder), {
                          onTitleClick: () => handleToggleEmailSelection(msg.uid),
                          onClassificationCorrect: (_emailId: string, _original: string, corrected: string) => {
                            handleClassificationChange(msg.uid, corrected);
                          },
                        })}
                      </div>
                    </div>
                  );
                }

                // Normal mode: draggable standard row with classification badge built-in
                // Single click opens email, drag moves it
                const classification = emailClassifications.get(msg.uid);
                return (
                  <div
                    key={msg.uid}
                    draggable
                    onDragStart={(e) => handleDragStart(e, msg.uid)}
                    onDragEnd={handleDragEnd}
                    style={{
                      opacity: draggingEmailUid === msg.uid ? 0.5 : 1,
                      cursor: 'pointer',
                    }}
                  >
                    {renderEntityRow(imapMessageToEmail(msg, classification, selectedFolder), {
                      onTitleClick: () => handleEmailClick(msg.uid),
                      onClassificationCorrect: (_emailId: string, _original: string, corrected: string) => {
                        // Update local classification for filtering
                        handleClassificationChange(msg.uid, corrected);
                      },
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </div>

      {/* Move to Folder Dialog */}
      {showMoveDialog && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowMoveDialog(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '0.75rem',
              padding: '1.5rem',
              width: '320px',
              maxHeight: '80vh',
              overflow: 'auto',
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem' }}>
              Sposta {selectedEmails.size} email in:
            </h3>
            <p style={{ margin: '0 0 1rem', fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
              Seleziona la cartella di destinazione
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              {folders
                .filter(f => f.path !== selectedFolder && !shouldHideFolder(f))
                .map(folder => (
                  <button
                    key={folder.path}
                    onClick={() => handleMoveEmails(folder.path)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.75rem',
                      background: 'transparent',
                      border: '1px solid var(--line)',
                      borderRadius: '0.5rem',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontSize: '0.875rem',
                      transition: 'background 0.1s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <Folder size={16} style={{ color: '#64748b' }} />
                    {getFriendlyFolderName(folder)}
                  </button>
                ))}
            </div>
            <button
              onClick={() => setShowMoveDialog(false)}
              style={{
                width: '100%',
                marginTop: '1rem',
                padding: '0.5rem',
                background: 'transparent',
                border: '1px solid var(--line)',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                fontSize: '0.875rem',
              }}
            >
              Annulla
            </button>
          </div>
        </div>
      )}

      {/* New Folder Dialog */}
      {showNewFolderDialog && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowNewFolderDialog(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '0.75rem',
              padding: '1.5rem',
              width: '320px',
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 1rem' }}>{t('email.newFolder')}</h3>
            {newFolderParent && (
              <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', margin: '0 0 0.5rem' }}>
                {t('email.inFolder')}: {newFolderParent}
              </p>
            )}
            <input
              type="text"
              value={newFolderName}
              onChange={e => setNewFolderName(e.target.value)}
              placeholder={t('email.folderNamePlaceholder')}
              autoFocus
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: '1px solid var(--line)',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') handleCreateFolder();
                if (e.key === 'Escape') setShowNewFolderDialog(false);
              }}
            />
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowNewFolderDialog(false)}
                style={{
                  padding: '0.5rem 1rem',
                  background: 'transparent',
                  border: '1px solid var(--line)',
                  borderRadius: '0.375rem',
                  cursor: 'pointer',
                }}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleCreateFolder}
                disabled={!newFolderName.trim()}
                style={{
                  padding: '0.5rem 1rem',
                  background: 'var(--brand)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  cursor: 'pointer',
                }}
              >
                {t('common.create')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSS */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

function FolderTreeNode({
  folder,
  selectedFolder,
  expandedFolders,
  onSelect,
  onToggleExpand,
  onCreateSubfolder,
  onRename,
  onDelete,
  menuOpen,
  setMenuOpen,
}: {
  folder: FolderNode;
  selectedFolder: string;
  expandedFolders: Set<string>;
  onSelect: (path: string) => void;
  onToggleExpand: (path: string) => void;
  onCreateSubfolder: (parentPath: string) => void;
  onRename: (path: string) => void;
  onDelete: (path: string) => void;
  menuOpen: string | null;
  setMenuOpen: (path: string | null) => void;
}) {
  const isSelected = folder.path === selectedFolder;
  const isExpanded = expandedFolders.has(folder.path);
  const hasChildren = folder.children.length > 0;
  const isSystemFolder = folder.specialUse && ['\\Inbox', '\\Sent', '\\Trash', '\\Drafts', '\\Junk'].includes(folder.specialUse);

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.25rem',
          padding: '0.375rem 0.5rem',
          paddingLeft: `${0.5 + folder.level * 1}rem`,
          borderRadius: '0.375rem',
          cursor: 'pointer',
          background: isSelected ? 'var(--brand)' : 'transparent',
          color: isSelected ? 'white' : 'var(--text)',
        }}
        onClick={() => onSelect(folder.path)}
      >
        {/* Expand toggle */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpand(folder.path);
          }}
          style={{
            width: '20px',
            height: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            opacity: hasChildren ? 1 : 0,
            pointerEvents: hasChildren ? 'auto' : 'none',
            color: 'inherit',
          }}
        >
          {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>

        {/* Icon */}
        <span style={{ opacity: 0.7 }}>{getFolderIcon(folder)}</span>

        {/* Label */}
        <span style={{ flex: 1, fontSize: '0.8125rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {folder.label || folder.name}
        </span>

        {/* Unread count */}
        {folder.messagesUnread > 0 && (
          <span
            style={{
              minWidth: '18px',
              height: '18px',
              borderRadius: '9px',
              background: isSelected ? 'white' : 'var(--brand)',
              color: isSelected ? 'var(--brand)' : 'white',
              fontSize: '0.625rem',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 0.25rem',
            }}
          >
            {folder.messagesUnread}
          </span>
        )}

        {/* Menu button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setMenuOpen(menuOpen === folder.path ? null : folder.path);
          }}
          style={{
            width: '20px',
            height: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            opacity: 0.5,
            color: 'inherit',
          }}
        >
          <MoreVertical size={14} />
        </button>
      </div>

      {/* Context menu */}
      {menuOpen === folder.path && (
        <div
          style={{
            marginLeft: `${0.5 + folder.level * 1}rem`,
            background: 'white',
            border: '1px solid var(--line)',
            borderRadius: '0.375rem',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            overflow: 'hidden',
          }}
        >
          <button
            onClick={() => {
              onCreateSubfolder(folder.path);
              setMenuOpen(null);
            }}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 0.75rem',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.8125rem',
              textAlign: 'left',
            }}
          >
            <FolderPlus size={14} /> New subfolder
          </button>
          {!isSystemFolder && (
            <>
              <button
                onClick={() => {
                  onRename(folder.path);
                  setMenuOpen(null);
                }}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 0.75rem',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '0.8125rem',
                  textAlign: 'left',
                }}
              >
                <Pencil size={14} /> Rename
              </button>
              <button
                onClick={() => {
                  onDelete(folder.path);
                  setMenuOpen(null);
                }}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 0.75rem',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '0.8125rem',
                  textAlign: 'left',
                  color: '#ef4444',
                }}
              >
                <Trash size={14} /> Delete
              </button>
            </>
          )}
        </div>
      )}

      {/* Children */}
      {isExpanded && folder.children.map(child => (
        <FolderTreeNode
          key={child.path}
          folder={child}
          selectedFolder={selectedFolder}
          expandedFolders={expandedFolders}
          onSelect={onSelect}
          onToggleExpand={onToggleExpand}
          onCreateSubfolder={onCreateSubfolder}
          onRename={onRename}
          onDelete={onDelete}
          menuOpen={menuOpen}
          setMenuOpen={setMenuOpen}
        />
      ))}
    </div>
  );
}

/**
 * MessageDetailContent - Content for DetailPanel (no header)
 * Used when email is opened as a modal overlay
 */
function MessageDetailContent({
  message,
  folder,
  folders,
  onMove,
  onDelete,
  onToggleRead,
  onToggleFlag,
}: {
  message: ImapMessageFull;
  folder: string;
  folders: ImapFolder[];
  onMove: (uid: number, toFolder: string) => void;
  onDelete: (uid: number) => void;
  onToggleRead: (uid: number, currentlyRead: boolean) => void;
  onToggleFlag: (uid: number, currentlyFlagged: boolean) => void;
}) {
  const { t } = useTranslation();
  const [showMoveMenu, setShowMoveMenu] = useState(false);
  const isRead = message.flags.includes('\\Seen');
  const isFlagged = message.flags.includes('\\Flagged');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Actions toolbar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.5rem 0',
        borderBottom: '1px solid var(--line)',
        marginBottom: '1rem',
      }}>
        <button
          onClick={() => onToggleFlag(message.uid, isFlagged)}
          title={isFlagged ? t('email.removeFlag') : t('email.flag')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.375rem',
            padding: '0.375rem 0.625rem',
            background: isFlagged ? '#fef3c7' : 'transparent',
            border: '1px solid var(--line)',
            borderRadius: '0.375rem',
            cursor: 'pointer',
            fontSize: '0.75rem',
            color: isFlagged ? '#f59e0b' : 'var(--text-muted)',
          }}
        >
          <Star size={14} fill={isFlagged ? '#f59e0b' : 'none'} />
          {isFlagged ? t('email.important') : t('email.flag')}
        </button>
        <button
          onClick={() => onToggleRead(message.uid, isRead)}
          title={isRead ? t('email.markAsUnread') : t('email.markAsRead')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.375rem',
            padding: '0.375rem 0.625rem',
            background: 'transparent',
            border: '1px solid var(--line)',
            borderRadius: '0.375rem',
            cursor: 'pointer',
            fontSize: '0.75rem',
          }}
        >
          {isRead ? <EyeOff size={14} /> : <Eye size={14} />}
          {isRead ? t('email.unread') : t('email.read')}
        </button>
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setShowMoveMenu(!showMoveMenu)}
            title={t('email.moveTo')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.375rem',
              padding: '0.375rem 0.625rem',
              background: 'transparent',
              border: '1px solid var(--line)',
              borderRadius: '0.375rem',
              cursor: 'pointer',
              fontSize: '0.75rem',
            }}
          >
            <FolderInput size={14} />
            {t('email.move')}
          </button>
          {showMoveMenu && (
            <div style={{
              position: 'absolute',
              left: 0,
              top: '100%',
              background: 'white',
              border: '1px solid var(--line)',
              borderRadius: '0.375rem',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              maxHeight: '200px',
              overflow: 'auto',
              zIndex: 10,
              minWidth: '160px',
            }}>
              {folders.filter(f => f.path !== folder).map(f => (
                <button
                  key={f.path}
                  onClick={() => {
                    onMove(message.uid, f.path);
                    setShowMoveMenu(false);
                  }}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.5rem 0.75rem',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '0.8125rem',
                    textAlign: 'left',
                  }}
                >
                  <Folder size={14} />
                  {f.label || f.name}
                </button>
              ))}
            </div>
          )}
        </div>
        <div style={{ flex: 1 }} />
        <button
          onClick={() => onDelete(message.uid)}
          title={t('common.delete')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.375rem',
            padding: '0.375rem 0.625rem',
            background: 'transparent',
            border: '1px solid #fecaca',
            borderRadius: '0.375rem',
            cursor: 'pointer',
            fontSize: '0.75rem',
            color: '#ef4444',
          }}
        >
          <Trash2 size={14} />
          {t('common.delete')}
        </button>
      </div>

      {/* Metadata */}
      <div style={{ marginBottom: '1rem' }}>
        <div style={{ marginBottom: '0.5rem' }}>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{t('email.from')}: </span>
          <span style={{ fontSize: '0.875rem' }}>
            {message.from?.name && <strong>{message.from.name}</strong>}
            {message.from?.name && ' '}
            &lt;{message.from?.address || t('email.unknown')}&gt;
          </span>
        </div>
        <div style={{ marginBottom: '0.5rem' }}>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{t('email.to')}: </span>
          <span style={{ fontSize: '0.875rem' }}>
            {message.to.map((addr, i) => (
              <span key={i}>{i > 0 && ', '}{addr.name || addr.address}</span>
            ))}
          </span>
        </div>
        {message.cc.length > 0 && (
          <div style={{ marginBottom: '0.5rem' }}>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{t('email.cc')}: </span>
            <span style={{ fontSize: '0.875rem' }}>
              {message.cc.map((addr, i) => (
                <span key={i}>{i > 0 && ', '}{addr.name || addr.address}</span>
              ))}
            </span>
          </div>
        )}
        <div>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{t('email.date')}: </span>
          <span style={{ fontSize: '0.875rem' }}>
            {new Date(message.date).toLocaleString('en-US')}
          </span>
        </div>
      </div>

      {/* Attachments */}
      {message.attachments.length > 0 && (
        <div style={{
          padding: '0.75rem',
          background: 'var(--bg)',
          borderRadius: '0.5rem',
          marginBottom: '1rem',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.5rem',
        }}>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', width: '100%', marginBottom: '0.25rem' }}>
            <Paperclip size={12} style={{ display: 'inline', marginRight: '0.25rem' }} />
            {t('email.attachmentCount', { count: message.attachments.length })}
          </span>
          {message.attachments.map((att, i) => (
            <div key={i} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.375rem',
              padding: '0.375rem 0.625rem',
              background: 'white',
              border: '1px solid var(--line)',
              borderRadius: '0.375rem',
              fontSize: '0.75rem',
            }}>
              <Paperclip size={12} />
              {att.filename}
              <span style={{ color: 'var(--text-muted)' }}>({Math.round(att.size / 1024)}KB)</span>
            </div>
          ))}
        </div>
      )}

      {/* Body */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {message.bodyHtml ? (
          <div
            dangerouslySetInnerHTML={{ __html: message.bodyHtml }}
            style={{ fontSize: '0.875rem', lineHeight: 1.6 }}
          />
        ) : (
          <pre style={{
            margin: 0,
            fontSize: '0.875rem',
            lineHeight: 1.6,
            whiteSpace: 'pre-wrap',
            fontFamily: 'inherit',
          }}>
            {message.body}
          </pre>
        )}
      </div>
    </div>
  );
}

/**
 * MessageDetailView - Full-page view (legacy, kept for reference)
 */
function MessageDetailView({
  message,
  folder,
  folders,
  onBack,
  onMove,
  onDelete,
  onToggleRead,
  onToggleFlag,
}: {
  message: ImapMessageFull;
  folder: string;
  folders: ImapFolder[];
  onBack: () => void;
  onMove: (uid: number, toFolder: string) => void;
  onDelete: (uid: number) => void;
  onToggleRead: (uid: number, currentlyRead: boolean) => void;
  onToggleFlag: (uid: number, currentlyFlagged: boolean) => void;
}) {
  const { t } = useTranslation();
  const [showMoveMenu, setShowMoveMenu] = useState(false);
  const isRead = message.flags.includes('\\Seen');
  const isFlagged = message.flags.includes('\\Flagged');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '0.75rem 1rem',
        borderBottom: '1px solid var(--line)',
      }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', padding: '0.25rem', cursor: 'pointer' }}>
          <ArrowLeft size={20} />
        </button>
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <h2 style={{
            margin: 0, fontSize: '1rem', fontWeight: 600,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {message.subject || t('email.noSubject')}
          </h2>
        </div>

        {/* Actions */}
        <button
          onClick={() => onToggleFlag(message.uid, isFlagged)}
          title={isFlagged ? t('email.removeFlag') : t('email.flag')}
          style={{
            padding: '0.375rem',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: isFlagged ? '#f59e0b' : 'var(--text-muted)',
          }}
        >
          <Star size={18} fill={isFlagged ? '#f59e0b' : 'none'} />
        </button>
        <button
          onClick={() => onToggleRead(message.uid, isRead)}
          title={isRead ? t('email.markAsUnread') : t('email.markAsRead')}
          style={{
            padding: '0.375rem',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          {isRead ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setShowMoveMenu(!showMoveMenu)}
            title={t('email.moveTo')}
            style={{
              padding: '0.375rem',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            <FolderInput size={18} />
          </button>
          {showMoveMenu && (
            <div style={{
              position: 'absolute',
              right: 0,
              top: '100%',
              background: 'white',
              border: '1px solid var(--line)',
              borderRadius: '0.375rem',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              maxHeight: '200px',
              overflow: 'auto',
              zIndex: 10,
              minWidth: '160px',
            }}>
              {folders.filter(f => f.path !== folder).map(f => (
                <button
                  key={f.path}
                  onClick={() => {
                    onMove(message.uid, f.path);
                    setShowMoveMenu(false);
                  }}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.5rem 0.75rem',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '0.8125rem',
                    textAlign: 'left',
                  }}
                >
                  <Folder size={14} />
                  {f.label || f.name}
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          onClick={() => onDelete(message.uid)}
          title={t('common.delete')}
          style={{
            padding: '0.375rem',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: '#ef4444',
          }}
        >
          <Trash2 size={18} />
        </button>
      </div>

      {/* Metadata */}
      <div style={{ padding: '1rem', borderBottom: '1px solid var(--line)' }}>
        <div style={{ marginBottom: '0.5rem' }}>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{t('email.from')}: </span>
          <span style={{ fontSize: '0.875rem' }}>
            {message.from?.name && <strong>{message.from.name}</strong>}
            {message.from?.name && ' '}
            &lt;{message.from?.address || t('email.unknown')}&gt;
          </span>
        </div>
        <div style={{ marginBottom: '0.5rem' }}>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{t('email.to')}: </span>
          <span style={{ fontSize: '0.875rem' }}>
            {message.to.map((addr, i) => (
              <span key={i}>{i > 0 && ', '}{addr.name || addr.address}</span>
            ))}
          </span>
        </div>
        {message.cc.length > 0 && (
          <div style={{ marginBottom: '0.5rem' }}>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{t('email.cc')}: </span>
            <span style={{ fontSize: '0.875rem' }}>
              {message.cc.map((addr, i) => (
                <span key={i}>{i > 0 && ', '}{addr.name || addr.address}</span>
              ))}
            </span>
          </div>
        )}
        <div>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{t('email.date')}: </span>
          <span style={{ fontSize: '0.875rem' }}>
            {new Date(message.date).toLocaleString('en-US')}
          </span>
        </div>
      </div>

      {/* Attachments */}
      {message.attachments.length > 0 && (
        <div style={{
          padding: '0.75rem 1rem',
          borderBottom: '1px solid var(--line)',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.5rem',
        }}>
          {message.attachments.map((att, i) => (
            <div key={i} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.375rem',
              padding: '0.375rem 0.625rem',
              background: 'var(--bg)',
              borderRadius: '0.375rem',
              fontSize: '0.75rem',
            }}>
              <Paperclip size={12} />
              {att.filename}
              <span style={{ color: 'var(--text-muted)' }}>({Math.round(att.size / 1024)}KB)</span>
            </div>
          ))}
        </div>
      )}

      {/* Body */}
      <div style={{ flex: 1, overflow: 'auto', padding: '1rem' }}>
        {message.bodyHtml ? (
          <div
            dangerouslySetInnerHTML={{ __html: message.bodyHtml }}
            style={{ fontSize: '0.875rem', lineHeight: 1.6 }}
          />
        ) : (
          <pre style={{
            margin: 0,
            fontSize: '0.875rem',
            lineHeight: 1.6,
            whiteSpace: 'pre-wrap',
            fontFamily: 'inherit',
          }}>
            {message.body}
          </pre>
        )}
      </div>
    </div>
  );
}

export default HubEmailChannel;
